export const MIN_PASSWORD_LENGTH = 8;

// Deliberately no character-class rule: NIST SP 800-63B advises against them,
// and length plus a breach check does the real work. If the Supabase project ever
// turns character requirements on, add them here too — this text is the only
// place the rules are stated to clients.
export const PASSWORD_RULES = `At least ${MIN_PASSWORD_LENGTH} characters, and not a password that has appeared in a known data breach.`;

export const PASSWORD_TIP = 'Three unrelated words make a password that is both strong and easy to remember.';

export function validateNewPassword(password, confirm) {
  if ((password || '').length < MIN_PASSWORD_LENGTH) {
    return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`;
  }
  if (password !== confirm) {
    return 'Passwords do not match.';
  }
  return null;
}

export const PASSWORD_BREACH_MESSAGE =
  `That password has appeared in a known data breach, so it is not safe to use here. Please choose a different one: ${PASSWORD_TIP.toLowerCase()}`;

// Supabase only runs its own breach check on the paid plans, and this project is
// not on one, so the promise in PASSWORD_RULES would otherwise be empty. Do it
// here against the same source Supabase uses.
//
// k-anonymity, so the password never leaves the browser: hash it with SHA-1, send
// only the first five characters of that hash, and compare the returned suffixes
// locally. HaveIBeenPwned never sees enough to identify the password.
//
// Fails OPEN. If the service is slow or unreachable the password is accepted:
// Carole is the entire support desk, and a client blocked at 9pm by someone
// else's outage becomes a phone call to her.
export async function isPasswordBreached(password, { fetchImpl, timeoutMs = 4000 } = {}) {
  const doFetch = fetchImpl || (typeof fetch === 'function' ? fetch : null);
  const subtle = globalThis.crypto?.subtle;
  if (!password || !doFetch || !subtle) return false;

  const controller = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    const digest = await subtle.digest('SHA-1', new TextEncoder().encode(password));
    const hash = Array.from(new Uint8Array(digest))
      .map(b => b.toString(16).padStart(2, '0'))
      .join('')
      .toUpperCase();
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);

    const res = await doFetch(`https://api.pwnedpasswords.com/range/${prefix}`, {
      signal: controller?.signal,
    });
    if (!res.ok) return false;
    const body = await res.text();
    // Each line is "SUFFIX:count". A match at all means it has been breached;
    // the count only matters if we ever want to soften this to a warning.
    return body.split('\n').some(line => line.split(':')[0]?.trim() === suffix);
  } catch {
    return false;
  } finally {
    if (timer) clearTimeout(timer);
  }
}

function countCharacterTypes(value) {
  return [/[a-z]/, /[A-Z]/, /[0-9]/, /[^a-zA-Z0-9]/].filter(re => re.test(value)).length;
}

export function passwordStrength(password) {
  const value = password || '';
  if (!value) return null;
  if (value.length < MIN_PASSWORD_LENGTH) {
    return { level: 'short', tone: 'danger', label: 'Too short', hint: `${MIN_PASSWORD_LENGTH} characters minimum.` };
  }
  // Length leads. A mix of character types only lifts a password that is already
  // long — it never rescues a short one, which is exactly how Carole1! is scored.
  const types = countCharacterTypes(value);
  if (value.length >= 15 || (value.length >= 12 && types >= 2)) {
    return { level: 'strong', tone: 'success', label: 'Strong', hint: '' };
  }
  if (value.length >= 12) {
    return { level: 'good', tone: 'success', label: 'Good', hint: 'A little longer would be stronger still.' };
  }
  return { level: 'ok', tone: 'warning', label: 'Meets the minimum', hint: 'Longer would be stronger.' };
}

// Supabase rejects breached and (if enabled) non-compliant passwords server-side.
// Its raw messages are not written for clients, so say it in plain words instead.
export function passwordErrorMessage(err) {
  if (!err) return '';
  const reasons = err.reasons || [];
  if (err.code === 'same_password') {
    return 'That is your current password. Please choose a different one.';
  }
  if (reasons.includes('pwned') || err.name === 'AuthWeakPasswordError' || err.code === 'weak_password') {
    if (reasons.includes('length')) {
      return `That password is too short. It needs at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (reasons.includes('characters')) {
      // Only reachable if the Supabase project has required character types
      // switched on, which contradicts the rules stated above the box. Name the
      // requirement so a client isn't left guessing what to change.
      return 'That password needs a mix of lower case, capitals, numbers and a symbol such as ! or ?.';
    }
    return PASSWORD_BREACH_MESSAGE;
  }
  if (err.name === 'AuthSessionMissingError') {
    return 'This link has expired or has already been used. Ask Carole to send you a new one.';
  }
  return err.message || 'Failed to update password.';
}
